// @flow
/* eslint react/jsx-no-bind:0 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import AlternativesSeparator from 'app/common/react/components/growth/unauth/signup/AlternativesSeparator/AlternativesSeparator';
import CONST from 'app/common/lib/Const';
import ContextLogger from 'app/common/lib/ContextLogger';
import i18n from 'app/common/lib/i18n';
import login from 'app/common/react/components/growth/unauth/signup/UserLogin';
import PhoneAuth from 'app/common/react/components/growth/unauth/signup/PhoneAuth/PhoneAuth';
import PropTypes from 'prop-types';
import RegisterForm from 'app/common/react/components/growth/unauth/signup/RegisterForm/RegisterForm';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import runValidators from 'app/common/react/components/growth/unauth/lib/ValidationUtils';
import storage from 'app/common/lib/Storage';
import templateConst from 'app/common/lib/templateConst';
import TermsOfService from 'app/common/react/components/growth/unauth/signup/TermsOfService/TermsOfService';
import touchableErrorMessage from 'app/common/react/components/growth/unauth/signup/AccountRecovery/TouchableErrorMessage';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { LoginCreds } from 'app/packages/pinterest-flow-types/authentication';
import type { SignupCredentialsAndLoggingData } from 'app/packages/pinterest-flow-types/registration';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import { addUsersLoggedIn } from 'app/common/lib/MultipleAccountsUtils';
import { BoldFontFamily } from 'app/common/react/components/common/FontFamily';
import { compose } from 'redux';
import { Experiment } from 'app/common/react/components/experiments';
import { RecaptchaTokenConsumer } from 'app/www-unauth/components/App/RecaptchaToken';
import { Route } from 'app/common/lib/ReactRouter/route';
import { showError } from 'app/common/react/components/growth/unauth/lib/ErrorUtils';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import { trackMagicLink } from 'app/common/lib/MagicLinkUtils';
import { type RequestContextType } from 'app/common/react/components/RequestContext';
import { withRouter } from 'react-router';
import AccountRecovery, {
  ACCT_RECOVERY_MODAL_MAPPING,
} from 'app/common/react/components/growth/unauth/signup/AccountRecovery/AccountRecovery';
import FacebookConnectButton, {
  FACEBOOK_IDENTIFIER,
} from 'app/common/react/components/growth/unauth/signup/FacebookConnectButton/FacebookConnectButton';
import GoogleConnectButton, {
  GOOGLE_IDENTIFIER,
} from 'app/common/react/components/growth/unauth/signup/GoogleConnectButton/GoogleConnectButton';
import loginUtil, { MODAL_TYPES, type ModalTypes } from 'app/www-unauth/lib/loginUtil';
import { ContainsOnlyAscii, Email, Password, Required } from 'app/common/lib/react/validator';

const cannonicalPasswordResetUrl = templateConst.settings.PASSWORD_RESET_URL;
const pageName = 'loginmodal';
const EMAIL_SIGNUP_IDENTIFIER: 'email' = 'email';
const RECAPTCHA_SITE_KEY = '6LeZIo0UAAAAAKFpimHio-ff_ddIT8f_QzpXG0_1';

const styles = {
  alternativeSeparatorContainer: {
    marginBottom: '9px',
  },
  emailButtonStyles: {
    height: '40px',
    width: '100%',
    borderRadius: '2px',
  },
  emailButtonTextStyles: {
    fontFamily: BoldFontFamily,
    textAlign: 'center',
    fontSize: '15px',
    WebkitFontSmoothing: 'auto', // make the font style in consitent with facebook and google buttons
  },
  spaceBetweenTextAndSocial: {
    height: '11px',
  },
  passwordLinkContainer: {
    float: 'left',
    marginBottom: '8px',
    marginTop: '4px',
  },
  loginForm: {
    borderTop: 'solid 1px #ccc',
    minHeight: '350px',
    padding: '20px 10px',
  },
  loginTitleContainer: {
    margin: '0 auto 25px',
  },
  loginTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    letterSpacing: '-1px',
  },
  loginContainer: {
    margin: '0 auto',
    width: '268px',
  },
  loginFooterContainer: {
    marginTop: '15px',
    color: '#333',
  },
  socialButtonContainer: {
    marginTop: '10px',
  },
  socialButtonSeparator: {
    height: '10px',
  },
  verticalCenter: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
};

export type LoginIdentifier =
  | typeof GOOGLE_IDENTIFIER
  | typeof FACEBOOK_IDENTIFIER
  | typeof EMAIL_SIGNUP_IDENTIFIER;

type OwnProps = {|
  clearPassword?: () => void,
  container: string,
  disableEmailLogin: boolean,
  disablePostSignupNextUrl: boolean,
  email: string,
  emailValidationError: string,
  hideEmailField: boolean,
  hideFacebookButton: boolean,
  hideGoogleButton: boolean,
  hidePasswordField: boolean,
  inputStyleOverrides?: {},
  isAccountSwitch: boolean,
  isFromAccountSwitchModal: boolean,
  isPersonalizedLoginFlyout?: boolean,
  isPersonalizedModal?: boolean,
  hidePasswordReset: boolean,
  hideTermsOfService: boolean,
  loadingIndicatorCallback: (boolean) => void,
  loginButtonTextOverride: string,
  magicLinkGroup: string,
  nextUrlParam: string,
  onEmailChange: (event: SyntheticEvent<>) => void,
  onLoginWithMfa: (LoginCreds, Object) => void,
  onPasswordChange?: (event: SyntheticEvent<>) => void,
  onSocialConnectVerified?: (
    network: string,
    userData: Object,
    credentialsAndLoggingData: SignupCredentialsAndLoggingData,
  ) => void,
  password: string,
  passwordValidationError: string,
  recaptchaToken?: string,
  recaptchaTokenV3?: string,
  shouldPreventRegister: boolean,
  toggleMagicLinkConfirmation: (boolean) => void,
  triggerAccountRecoveryMode?: (string) => void,
  showLoginWelcomeLoadingFn?: (boolean) => void,
  showResponseMessageModal?: (isSignup: boolean) => void,
  onFBPhoneOnlyConnectVerified?: (
    network: string,
    userData: Object,
    credentialsAndLoggingData: Object,
  ) => void,
|};

type ReduxProps = {|
  viewer: Viewer,
|};

type HOCProps = {|
  requestContext: RequestContextType,
  routes: Array<Route>,
|};

type Props = {|
  ...OwnProps,
  ...ReduxProps,
  ...HOCProps,
|};

type State = {|
  accountRecoveryModalType: ?ModalTypes,
  emailValidationError: ?(string | React$Element<any>),
  passwordValidationError: ?(string | React$Element<any>),
  facebookValidationError: ?boolean,
  googleValidationError: ?string,
  loading: boolean,
  magicLinkLoading: boolean,
  passwordFieldVisible: boolean,
  isExistingEmail: boolean,
  isStopRequest: boolean,
|};

export const Login = (props: Props) => (
  <RecaptchaTokenConsumer>
    {(recaptchaTokenV3) => <LoginStep {...props} recaptchaTokenV3={recaptchaTokenV3} />}
  </RecaptchaTokenConsumer>
);

Login.defaultProps = {
  container: 'unknown',
  isAccountSwitch: false,
  isPersonalizedLoginFlyout: false,
  shouldPreventRegister: false,
};

export class LoginStep extends Component<Props, State> {
  constructor(props: Props, context: Object) {
    super(props, context);

    this.state = {
      accountRecoveryModalType: null,
      emailValidationError: props.emailValidationError || null,
      passwordValidationError: props.passwordValidationError || null,
      facebookValidationError: null,
      googleValidationError: null,
      loading: false,
      magicLinkLoading: false,
      passwordFieldVisible: false,
      isExistingEmail: false,
      isStopRequest: false,
    };

    const emailRequiredValidatorMessage = i18n._(
      "You missed a spot! Don't forget to add your email or phone number.",
    );
    const malformedEmailValidatorMessage = i18n._(
      "Hmm...that doesn't look like an email address or phone number.",
    );
    const asciiOnlyPasswordValidatorMessage = i18n._('Oh @*&#! No zany characters allowed.');

    this.emailValidators = [
      Required({ message: emailRequiredValidatorMessage }),
      Email({ message: malformedEmailValidatorMessage }),
    ];
    const message = i18n._('The password you entered is incorrect.');

    this.passwordValidators = [
      Password({
        shortPassStr: message,
        badPassStyle: message,
        samePasswordStr: message,
        blackPassStr: message,
      }),
    ];

    this.asciiValidators = [ContainsOnlyAscii({ message: asciiOnlyPasswordValidatorMessage })];
  }

  componentDidMount() {
    if (
      loginUtil.isFullyLoggedIn(this.context.isAuthenticated, this.context.loginState) &&
      !this.props.isAccountSwitch
    ) {
      if (this.props.showResponseMessageModal) {
        this.props.showResponseMessageModal(false);
      } else {
        loginUtil.handleRedirect(this.props.nextUrlParam);
      }
    }
    PhoneAuth.setupPhoneAuth(this.context.locale);

    window.onloadCallback = this.onloadCallback;
    window.verifyCaptcha = this.verifyCaptcha;

    if (typeof window.recaptchaCount === 'undefined') {
      window.recaptchaCount = 1;
    } else {
      window.recaptchaCount += 1;
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const emailValidationError = nextProps.emailValidationError;
    const passwordValidationError = nextProps.passwordValidationError;
    let nextState = {};

    if (emailValidationError !== this.props.emailValidationError) {
      nextState = { emailValidationError };
    } else if (passwordValidationError !== this.props.passwordValidationError) {
      nextState = { passwordValidationError };
    }
    this.setState(nextState);
  }

  componentWillUnmount() {
    if (typeof window.recaptchaCount !== 'undefined') {
      window.recaptchaCount -= 1;
    }
  }

  onLoginWithVoluntarilyDeactivated = (error: Object) => {
    this.setState({
      googleValidationError: error.message,
    });
  };

  onLoginWithSuspended = () => {
    const { loadingIndicatorCallback, showLoginWelcomeLoadingFn } = this.props;
    if (loadingIndicatorCallback) {
      loadingIndicatorCallback(false);
    }
    if (showLoginWelcomeLoadingFn) {
      showLoginWelcomeLoadingFn(false);
    }

    this.setState({
      accountRecoveryModalType: ACCT_RECOVERY_MODAL_MAPPING[CONST.POLICY_VIOLATION_USER],
      loading: false,
    });
  };

  onloadCallback = () => {
    try {
      window.recaptchaIdOne = window.grecaptcha.render('recaptcha_placeholder_1', {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: 'verifyCaptcha',
      });
    } catch (e) {
      // Skip the already loaded error
    }

    try {
      window.recaptchaIdTwo = window.grecaptcha.render('recaptcha_placeholder_2', {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: 'verifyCaptcha',
      });
    } catch (e) {
      // Skip the already loaded error
    }
  };

  getAuxDataLoginMethod(loginMethodIdentifier: LoginIdentifier) {
    if (loginMethodIdentifier === FACEBOOK_IDENTIFIER) {
      return PCONST.SignupLoginMethods.FACEBOOK;
    } else if (loginMethodIdentifier === GOOGLE_IDENTIFIER) {
      return PCONST.SignupLoginMethods.GPLUS;
    } else if (loginMethodIdentifier === EMAIL_SIGNUP_IDENTIFIER) {
      return PCONST.SignupLoginMethods.EMAIL;
    }
    return '';
  }

  asciiValidators: Array<any>;
  emailValidators: Array<any>;
  passwordValidators: Array<any>;

  handleButtonClick = (clickEvent: ?SyntheticMouseEvent<>, recaptchaToken?: string) => {
    trackRegisterAction('unauth.login.button.clicked');

    const { viewer } = this.props;
    let error = PhoneAuth.runPhoneValidators(
      this.props.email,
      this.context.locale,
      this._loginFacebookAccountKit.bind(this),
    );

    if (viewer.isAuth) {
      track(PCONST.thrift.EventType.CLICK, {
        element: PCONST.thrift.ElementType.LOGIN_BUTTON,
        login_state: viewer.isLimitedLogin
          ? PCONST.LoginStates.LIMITED
          : PCONST.LoginStates.DEFAULT,
        signup_login_method: PCONST.SignupLoginMethods.EMAIL,
      });
    } else {
      track(PCONST.thrift.EventType.CLICK, {
        element: PCONST.thrift.ElementType.LOGIN_BUTTON,
        signup_login_method: PCONST.SignupLoginMethods.EMAIL,
      });
    }

    this.setState({ facebookValidationError: null, googleValidationError: null });
    if (error) {
      error = runValidators(this.props.email, this.emailValidators);
      if (error) {
        this.setState({ emailValidationError: error });
        trackRegisterAction(`unauth.login.error.CLIENT_ERROR.malformemail.${pageName}`);
      } else {
        this.setState({ emailValidationError: null });
        error = runValidators(this.props.password, this.passwordValidators);
        // Note: we are splitting ascii check into its own validator to gauge specific impact
        // TODO(rmist): add ascii validator back to passwordValidators if we no longer need to log
        const asciiError = runValidators(this.props.password, this.asciiValidators);
        if (error) {
          this.setState({ passwordValidationError: error });
          trackRegisterAction(`unauth.login.error.CLIENT_ERROR.malformpwd.${pageName}`);
        } else if (asciiError) {
          // Still log as error
          this.setState({ passwordValidationError: asciiError });
          trackRegisterAction(`unauth.login.error.CLIENT_ERROR.malformpwd.${pageName}`);
          // Log specific non-ascii errors
          trackRegisterAction(`unauth.login.error.CLIENT_ERROR.nonasciipwd.${pageName}`);
        } else {
          this.setState({ passwordValidationError: null });
          this.loginUser(EMAIL_SIGNUP_IDENTIFIER, undefined, recaptchaToken);
        }
      }
    }
  };

  preventRegister = (loginWith: LoginIdentifier) => {
    if (loginWith === FACEBOOK_IDENTIFIER) {
      this.updateAccountRecoveryModal(MODAL_TYPES.PREVENT_FACEBOOK_REGISTER);
    } else if (loginWith === GOOGLE_IDENTIFIER) {
      this.updateAccountRecoveryModal(MODAL_TYPES.PREVENT_GOOGLE_REGISTER);
    }

    if (this.props.triggerAccountRecoveryMode) {
      this.props.triggerAccountRecoveryMode(loginWith);
    }
  };

  updateAccountRecoveryModal = (modalType: ?ModalTypes) =>
    this.setState({ accountRecoveryModalType: modalType });

  handleLoginFailure = (loginMethod: LoginIdentifier, error: Object, creds: LoginCreds) => {
    const {
      clearPassword,
      loadingIndicatorCallback,
      showLoginWelcomeLoadingFn,
      viewer,
    } = this.props;

    const auxDataLoginMethod = this.getAuxDataLoginMethod(loginMethod);
    trackRegisterAction(`unauth.login.error.LOGIN_FAILURE.${loginMethod}`);

    if (viewer.isAuth) {
      track(PCONST.thrift.EventType.LOGIN_FAILURE, {
        login_state: viewer.isLimitedLogin
          ? PCONST.LoginStates.LIMITED
          : PCONST.LoginStates.DEFAULT,
        signup_login_method: auxDataLoginMethod,
      });
    } else {
      track(PCONST.thrift.EventType.LOGIN_FAILURE, {
        signup_login_method: auxDataLoginMethod,
      });
    }

    loadingIndicatorCallback(false);
    if (showLoginWelcomeLoadingFn) {
      showLoginWelcomeLoadingFn(false);
    }

    const api_error_code =
      error.http_status === CONST.RESPONSE_CODE_TOO_MANY_REQUESTS
        ? CONST.RESPONSE_CODE_TOO_MANY_REQUESTS
        : error.api_error_code;

    if (api_error_code === CONST.LOGIN_MFA_REQUIRED) {
      loginUtil.handleLoginMfa(creds, error.data, this.props.onLoginWithMfa);
      return;
    }

    if (
      api_error_code === CONST.RESPONSE_CODE_TOO_MANY_REQUESTS &&
      error.api_error_code === CONST.API_EVENT_BLOCKED_ERROR &&
      error.message &&
      // Flag set for ip based rate limit
      error.message[error.message.length - 1] !== '!' &&
      !loginUtil.isExceedRecaptchaLocalLimit()
    ) {
      if (document.getElementById('recaptcha-jssdk')) {
        this.onloadCallback();
      } else {
        const js = document.createElement('script');
        js.id = 'recaptcha-jssdk';
        js.src = 'https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit';
        const fjs = document.getElementsByTagName('script')[0];
        if (fjs.parentNode) {
          fjs.parentNode.insertBefore(js, fjs);
        }
      }
      this.setState({
        passwordValidationError: i18n._(
          'Oops! You logged in too quickly. Please try again with the reCAPTCHA.',
          'Error message showing to user when his/her login attempt exceeds ip based rules, ask they to retry' +
            ' using recaptcha',
        ),
        loading: false,
      });
      return;
    }

    const errorState = loginUtil.getLoginErrorState(
      error,
      'login',
      this.props.email,
      touchableErrorMessage,
      this.updateAccountRecoveryModal,
    );

    if (errorState) {
      this.setState({
        accountRecoveryModalType: ACCT_RECOVERY_MODAL_MAPPING[api_error_code],
        ...errorState,
        loading: false,
      });
      if (clearPassword && errorState.passwordValidationError) {
        clearPassword();
      }
    } else {
      // wait for translation with new context
      i18n._(
        'Sorry! Something went wrong on our end.',
        "Error message shown when user tried to login but error occured, and we don't have any other information about the error. May or may not be recoverable by the user.",
      );
      showError(i18n._('Sorry! Something went wrong on our end.'));
      this.setState({ loading: false });
    }
  };

  verifyCaptcha = () => {
    const response =
      window.grecaptcha.getResponse(window.recaptchaIdOne) ||
      window.grecaptcha.getResponse(window.recaptchaIdTwo);
    loginUtil.updateRecaptchaTimeStamp();
    this.handleButtonClick(undefined, response);
    if (!loginUtil.isExceedRecaptchaLocalLimit()) {
      if (typeof window.recaptchaIdOne !== 'undefined') {
        window.grecaptcha.reset(window.recaptchaIdOne);
      }
      if (typeof window.recaptchaIdTwo !== 'undefined') {
        window.grecaptcha.reset(window.recaptchaIdTwo);
      }
    }
  };

  _loginFacebookAccountKit = (response: Object) => {
    if (PhoneAuth.validateResponse(response)) {
      // successful Account Kit flow, perform Pinterest login
      const creds = {
        phone_number: this.props.email,
        fb_auth_code: response.code,
      };
      trackRegisterAction('unauth_phone_only_login_attempt');
      this.loginUser(FACEBOOK_IDENTIFIER, creds);
    } else {
      // user closed Account Kit popup
      this.setState({
        emailValidationError: i18n._('Enter your email or phone number to try again.'),
        loading: false,
      });
    }
  };

  handleEmailChange = (e: SyntheticEvent<>) => {
    // call parent onEmailChange
    if (this.props.onEmailChange) {
      this.props.onEmailChange(e);
    }
  };

  handlePasswordChange = (e: SyntheticEvent<>) => {
    if (this.state.passwordValidationError) {
      this.setState({ passwordValidationError: null });
    }
    if (this.props.onPasswordChange) {
      this.props.onPasswordChange(e);
    }
  };

  loginUser(loginMethod: LoginIdentifier, altCreds?: LoginCreds, recaptchaToken?: string) {
    const {
      container,
      hideEmailField,
      isAccountSwitch,
      requestContext,
      showLoginWelcomeLoadingFn,
      showResponseMessageModal,
      viewer,
    } = this.props;
    const { isExistingEmail } = this.state;
    this.setState({ loading: true });
    this.props.loadingIndicatorCallback(true);

    const isLoginWelcomeExpEligible = !viewer.isAuth && (hideEmailField || isExistingEmail);
    if (isLoginWelcomeExpEligible && showLoginWelcomeLoadingFn) {
      const inLoginWelcomeExp = new SingleTreatmentExp(
        requestContext,
        'web_login_welcome_email',
      ).enabled();
      requestContext.experiments.flush();
      if (inLoginWelcomeExp) {
        showLoginWelcomeLoadingFn(true);
      }
    }

    const creds = altCreds || {
      username_or_email: this.props.email,
      password: this.props.password,
    };
    const loggingData = {
      container: this.props.container,
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
    };

    const auxDataLoginMethod = this.getAuxDataLoginMethod(loginMethod);

    if (viewer.isAuth) {
      track(PCONST.thrift.EventType.LOGIN_ATTEMPT, {
        login_state: viewer.isLimitedLogin
          ? PCONST.LoginStates.LIMITED
          : PCONST.LoginStates.DEFAULT,
        signup_login_method: auxDataLoginMethod,
      });
    } else {
      track(PCONST.thrift.EventType.LOGIN_ATTEMPT, {
        signup_login_method: auxDataLoginMethod,
      });
    }

    if (isAccountSwitch) {
      // Important: pause and flush impressions before switching accounts, so that
      // the impressions get logged under the correct user. See BUG-96331.
      ContextLogger.getInstance().pauseImpressionsAndFlush();
    }

    login(
      creds,
      loggingData,
      this.props.requestContext.seoUnauthExperiments,
      isAccountSwitch,
      recaptchaToken,
      this.props.recaptchaTokenV3 || 'default',
    ).then(
      (response = {}) => {
        if (viewer.isAuth) {
          track(PCONST.thrift.EventType.LOGIN_SUCCESS, {
            login_state: viewer.isLimitedLogin
              ? PCONST.LoginStates.LIMITED
              : PCONST.LoginStates.DEFAULT,
            signup_login_method: auxDataLoginMethod,
          });
        } else {
          track(PCONST.thrift.EventType.LOGIN_SUCCESS, {
            signup_login_method: auxDataLoginMethod,
          });
        }

        loginUtil.storeLoginCredentialsToBrowser(creds);
        let nextUrl = this.props.nextUrlParam || loginUtil.defaultLoginRedirectUrl();

        if (
          isAccountSwitch &&
          response.resource_response &&
          response.resource_response.data &&
          response.resource_response.data.user
        ) {
          const user = response.resource_response.data.user;
          addUsersLoggedIn(user, 'email', container);
          nextUrl = '/' + user.username;
        } else {
          if (nextUrl !== '/') {
            storage.localStorage.setItem(PCONST.LOCALSTORAGE_AUTH_AFTER_LOGIN, true);
          }
          loginUtil.storeLoginCredentialsToBrowser(creds);
        }

        if (showResponseMessageModal) {
          showResponseMessageModal(false);
        } else {
          loginUtil.handleRedirect(nextUrl);
        }
      },
      (error) => this.handleLoginFailure(loginMethod, error, creds),
    );
  }

  handleBlur = () => {
    this.setState({ facebookValidationError: null, googleValidationError: null });
  };

  handleMagicLinkClick = () => {
    this.setState({ magicLinkLoading: true });
    const error = runValidators(this.props.email, this.emailValidators);
    if (error) {
      this.setState({ emailValidationError: error, magicLinkLoading: false });
    } else {
      this.setState({ emailValidationError: null });
      // Resource call to send email
      const resource = ResourceFactory.create('UserLoginPasswordlessResource', {
        username_or_email: this.props.email,
        is_magic_link: true,
      });
      resource.callCreate().then(
        () => {
          // On link sent success
          trackMagicLink('link_send_success', this.props.email, this.props.password);
          this.setState({ magicLinkLoading: false });
          this.props.toggleMagicLinkConfirmation(true);
        },
        (error) => {
          // Link failed
          trackMagicLink('link_send_failure', this.props.email, this.props.password);
          let errorMessage;
          if (!error.message) {
            errorMessage = i18n._(
              'Sorry! Something went wrong on our end.',
              'error message on login modal telling users we had a problem with their request',
            );
          } else {
            errorMessage = i18n._(
              'The email you entered does not belong to any account.',
              'Error message shown when login email does not exist.',
            );
          }
          this.setState({
            emailValidationError: errorMessage,
            magicLinkLoading: false,
          });
        },
      );
    }
  };

  checkEmailExistsWithCompletion = (
    email: string,
    handleEmailExists: () => void,
    handleEmailNotExist: () => void,
  ) => {
    const emailExistsResource = ResourceFactory.create('EmailExistsResource', {
      email,
    });
    emailExistsResource.callGet({ showError: false }).then((resourceResponse) => {
      if (resourceResponse.resource_response.data === false) {
        // Email not associated with existing account
        handleEmailNotExist();
      } else {
        // Email associated with existing accoutn
        handleEmailExists();
      }
    });
  };

  handleExistingEmail = (isExisting: boolean) => {
    this.setState({ isExistingEmail: isExisting });
  };

  renderSocialButtons(inLLSocialBeforeEmailExp?: boolean) {
    const {
      disablePostSignupNextUrl,
      hideFacebookButton,
      hideGoogleButton,
      isAccountSwitch,
      isPersonalizedLoginFlyout,
      loadingIndicatorCallback,
      nextUrlParam,
      onLoginWithMfa,
      onSocialConnectVerified,
      shouldPreventRegister,
      showLoginWelcomeLoadingFn,
      onFBPhoneOnlyConnectVerified,
    } = this.props;

    const facebookButton = !hideFacebookButton && (
      <FacebookConnectButton
        buttonText={i18n._('Continue with Facebook')}
        container={this.props.container}
        disablePostSignupNextUrl={disablePostSignupNextUrl}
        isAccountSwitch={isAccountSwitch}
        loadingIndicatorCallback={loadingIndicatorCallback}
        nextUrlParam={nextUrlParam}
        onSocialConnectVerified={onSocialConnectVerified}
        onFBPhoneOnlyConnectVerified={onFBPhoneOnlyConnectVerified}
        onLoginWithMfa={onLoginWithMfa}
        onLoginWithSuspended={this.onLoginWithSuspended}
        tooltip={this.state.facebookValidationError}
        useGestaltTooltip
        preventRegister={shouldPreventRegister ? this.preventRegister.bind(this) : undefined}
        showResponseMessageModal={this.props.showResponseMessageModal}
        showLoginWelcomeLoadingFn={showLoginWelcomeLoadingFn}
      />
    );

    const googleButton = !hideGoogleButton && (
      <GoogleConnectButton
        container={this.props.container}
        disablePostSignupNextUrl={disablePostSignupNextUrl}
        isAccountSwitch={isAccountSwitch}
        nextUrlParam={nextUrlParam}
        onLoginWithMfa={onLoginWithMfa}
        onLoginWithSuspended={this.onLoginWithSuspended}
        onSocialConnectVerified={onSocialConnectVerified}
        tooltip={this.state.googleValidationError}
        onLoginWithVoluntarilyDeactivated={this.onLoginWithVoluntarilyDeactivated}
        preventRegister={shouldPreventRegister ? this.preventRegister.bind(this) : undefined}
        showResponseMessageModal={this.props.showResponseMessageModal}
        showLoginWelcomeLoadingFn={showLoginWelcomeLoadingFn}
      />
    );

    const showBoth = !hideFacebookButton && !hideGoogleButton;
    const overrideHeight = showBoth ? '8px' : '0px';

    let socialButtonSeparatorStyle = styles.socialButtonSeparator;
    let socialButtonContainerStyle = styles.socialButtonContainer;

    if (isPersonalizedLoginFlyout) {
      socialButtonSeparatorStyle = { ...styles.socialButtonSeparator, height: overrideHeight };
      socialButtonContainerStyle = { ...styles.socialButtonContainer, marginTop: '9px' };
    }

    if (inLLSocialBeforeEmailExp) {
      socialButtonSeparatorStyle = { ...styles.socialButtonSeparator, height: overrideHeight };
      socialButtonContainerStyle = { ...styles.socialButtonContainer, marginTop: '0px' };
    }

    return (
      <div style={socialButtonContainerStyle}>
        {facebookButton}
        <div style={socialButtonSeparatorStyle} />
        {googleButton}
      </div>
    );
  }

  renderPasswordResetLink = () => {
    const { email, viewer } = this.props;
    const { isExistingEmail, passwordValidationError } = this.state;

    // don't show reset password link when inline error contains a password reset link
    if (passwordValidationError && React.isValidElement(passwordValidationError)) {
      return null;
    }

    return (
      <div style={styles.passwordLinkContainer}>
        <a
          href={cannonicalPasswordResetUrl + (email ? `?q=${email}` : '')}
          onClick={() => {
            if (isExistingEmail) {
              trackRegisterAction('unauth_web.login_modal.forgot_password_with_existing_email');
            }
            trackRegisterAction('login_modal_forget_account');
            if (viewer.isAuth) {
              track(PCONST.thrift.EventType.CLICK, {
                element: PCONST.thrift.ElementType.FORGET_PW_LINK,
                login_state: viewer.isLimitedLogin
                  ? PCONST.LoginStates.LIMITED
                  : PCONST.LoginStates.DEFAULT,
              });
            } else {
              track(PCONST.thrift.EventType.CLICK, {
                element: PCONST.thrift.ElementType.FORGET_PW_LINK,
              });
            }
          }}
        >
          {i18n._(
            'Forgot your password?',
            'Desktop login modal link for users to recover their account',
          )}
        </a>
      </div>
    );
  };

  renderRegisterForm = () => {
    const {
      email,
      hideEmailField,
      inputStyleOverrides,
      isAccountSwitch,
      loginButtonTextOverride,
      magicLinkGroup,
      password,
    } = this.props;

    const loginButtonText = loginButtonTextOverride || i18n._('Log in');
    return (
      <RegisterForm
        buttonText={loginButtonText}
        checkEmailExistsWithCompletion={this.checkEmailExistsWithCompletion}
        disableEmailLogin={this.props.disableEmailLogin}
        email={email}
        emailText={i18n._('Email', 'Placeholder text for email field')}
        emailValidationError={this.state.emailValidationError}
        handleMagicLinkClick={this.handleMagicLinkClick}
        hideEmailField={hideEmailField}
        hidePasswordField={this.props.hidePasswordField}
        hideFacebookButton={this.props.hideFacebookButton}
        hideGoogleButton={this.props.hideGoogleButton}
        inputStyleOverrides={inputStyleOverrides}
        loading={this.state.loading && !isAccountSwitch}
        magicLinkLoading={this.state.magicLinkLoading}
        magicLinkGroup={magicLinkGroup}
        onButtonClick={this.handleButtonClick}
        onEmailBlur={this.handleBlur.bind(this)}
        onEmailChange={this.handleEmailChange.bind(this)}
        onPasswordChange={this.handlePasswordChange}
        password={password}
        passwordText={i18n._('Password')}
        passwordValidationError={this.state.passwordValidationError}
        renderPasswordResetLink={this.renderPasswordResetLink}
        handleExistingEmail={this.handleExistingEmail}
      />
    );
  };

  renderOriginalForm = () => {
    const {
      hideFacebookButton,
      hideGoogleButton,
      hidePasswordField,
      isPersonalizedLoginFlyout,
      isPersonalizedModal,
      viewer,
    } = this.props;

    const hasEmailAndSocialConnect =
      !hidePasswordField && (!hideGoogleButton || !hideFacebookButton);
    return (
      <Experiment
        name="web_limited_login_social_before_email"
        isEligible={
          viewer.isLimitedLogin &&
          hasEmailAndSocialConnect &&
          (isPersonalizedLoginFlyout || isPersonalizedModal)
        }
      >
        {({ anyEnabled }) => {
          if (anyEnabled && (isPersonalizedLoginFlyout || isPersonalizedModal)) {
            return (
              <React.Fragment>
                {this.renderSocialButtons(true)}
                <div style={styles.alternativeSeparatorContainer}>
                  <AlternativesSeparator />
                </div>
                {this.renderRegisterForm()}
              </React.Fragment>
            );
          } else {
            return (
              <React.Fragment>
                {this.renderRegisterForm()}
                {hasEmailAndSocialConnect && <AlternativesSeparator />}
                {this.renderSocialButtons(false)}
              </React.Fragment>
            );
          }
        }}
      </Experiment>
    );
  };

  renderButtons = () => this.renderOriginalForm();

  render() {
    const { email } = this.props;

    return (
      <div style={styles.loginContainer}>
        <AccountRecovery
          modalType={this.state.accountRecoveryModalType}
          updateAccountRecoveryModal={this.updateAccountRecoveryModal}
          email={email}
          onLoginWithMfa={this.props.onLoginWithMfa}
        />
        {this.renderButtons()}
        {!this.props.hideTermsOfService && <TermsOfService dark />}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState): ReduxProps => ({
  viewer: state.viewer,
});

LoginStep.contextTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  loginState: PropTypes.number,
  userAgent: PropTypes.shape({
    isMobile: PropTypes.bool.isRequired,
  }).isRequired,
  hybridTier: PropTypes.string,
  pageType: PropTypes.string,
};

export default compose(
  connect<_, OwnProps, _, _, _, _>(mapStateToProps),
  withRouter,
)(Login);
